import { useState, useEffect } from 'react';
import { Power, AlertTriangle, Save, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MaintenanceTab({ authToken }) {
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [subMessage, setSubMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const headers = { Authorization: `Bearer ${authToken}` };

  useEffect(() => {
    axios.get(`${API}/api/maintenance/settings`, { headers })
      .then(res => {
        setEnabled(res.data.enabled || false);
        setMessage(res.data.message || '');
        setSubMessage(res.data.sub_message || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/api/maintenance/settings`, {
        enabled,
        message,
        sub_message: subMessage
      }, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    const newState = !enabled;
    setEnabled(newState);
    setSaving(true);
    try {
      await axios.put(`${API}/api/maintenance/settings`, {
        enabled: newState,
        message,
        sub_message: subMessage
      }, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setEnabled(!newState);
      alert('Failed to toggle: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="maintenance-tab-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="maintenance-tab">
      {/* Status Banner */}
      <div className={`rounded-lg border p-4 flex items-center justify-between ${
        enabled 
          ? 'bg-amber-500/10 border-amber-500/30' 
          : 'bg-emerald-500/10 border-emerald-500/30'
      }`}>
        <div className="flex items-center gap-3">
          {enabled ? (
            <AlertTriangle className="h-5 w-5 text-amber-400" />
          ) : (
            <Power className="h-5 w-5 text-emerald-400" />
          )}
          <div>
            <p className={`font-semibold ${enabled ? 'text-amber-300' : 'text-emerald-300'}`}>
              {enabled ? 'Maintenance Mode is ON' : 'Site is Live'}
            </p>
            <p className="text-sm text-gray-400">
              {enabled 
                ? 'Visitors see the maintenance page. Only admins can access the dashboard.'
                : 'Your site is live and accessible to all visitors.'}
            </p>
          </div>
        </div>

        <button
          data-testid="maintenance-toggle-btn"
          onClick={handleToggle}
          disabled={saving}
          className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all ${
            enabled
              ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
              : 'bg-amber-600 hover:bg-amber-500 text-white'
          } disabled:opacity-50`}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : enabled ? (
            'Turn Off Maintenance'
          ) : (
            'Turn On Maintenance'
          )}
        </button>
      </div>

      {/* Message Editor */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-6 space-y-5">
        <h3 className="text-white font-semibold text-lg">Maintenance Message</h3>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Main Message
          </label>
          <input
            data-testid="maintenance-message-input"
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={500}
            placeholder="e.g. We're performing scheduled maintenance..."
            className="w-full px-4 py-3 rounded-lg bg-gray-900/70 border border-gray-600 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
          />
          <p className="text-xs text-gray-500 mt-1">{message.length}/500</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Sub Message (time estimate, etc.)
          </label>
          <input
            data-testid="maintenance-sub-message-input"
            type="text"
            value={subMessage}
            onChange={(e) => setSubMessage(e.target.value)}
            maxLength={500}
            placeholder="e.g. The site will be back up at 11:30 PM tonight."
            className="w-full px-4 py-3 rounded-lg bg-gray-900/70 border border-gray-600 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
          />
          <p className="text-xs text-gray-500 mt-1">{subMessage.length}/500</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="maintenance-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium text-sm transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Message
          </button>
          {saved && (
            <span className="text-emerald-400 text-sm animate-pulse">Saved!</span>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700/50 p-6">
        <h3 className="text-white font-semibold text-lg mb-4">Preview</h3>
        <div className="bg-gray-950 rounded-lg p-8 text-center space-y-3">
          <p className="text-white/80 text-sm leading-relaxed">
            {message || <span className="text-gray-600 italic">No main message set</span>}
          </p>
          <p className="text-white/50 text-sm">
            {subMessage || <span className="text-gray-600 italic">No sub message set</span>}
          </p>
        </div>
      </div>
    </div>
  );
}
